import React, { useState, useEffect, useRef } from 'react';
import { X, Search, CheckCircle, AlertCircle, Hash, Loader2, Info, AlertTriangle } from 'lucide-react';
import { PListLineDto } from '../../../../../types/picking';
import { stcTransService } from '../../../../../services/stcTransService';
import { pickingService } from '../../../../../services/pickingService';
import { ScanTextInput } from '../../../../common/inputs';
import { useTranslation } from '../../../../../contexts/I18nProvider';

// En dummy-serial er et auto-generert placeholder-serienummer som må erstattes før plukk.
// Format: MIG-{itemNo}-{timestamp}-{idx}
const isDummySerial = (serialNo: string): boolean =>
  serialNo.toUpperCase().startsWith('MIG-');

/**
 * Vises når søkefeltet har en verdi som ikke matcher noen eksisterende serie,
 * og det finnes dummies på lokasjonen. Gir operatør en tydelig vei til å
 * registrere det ekte serienummeret og plukke.
 */
const RegisterNewSerialBox: React.FC<{
  scanned: string;
  itemNo: string;
  hasDummies: boolean;
  confirming: boolean;
  onRegister: (scanned: string) => void;
}> = ({ scanned, itemNo, hasDummies, confirming, onRegister }) => {
  const { t } = useTranslation();
  if (!scanned) {
    return (
      <div className="text-center text-gray-500 py-8">
        {t('warehouse.modules.picking.serialPick.noSerialsAvailable')}
      </div>
    );
  }

  if (!hasDummies) {
    return (
      <div className="text-center text-gray-500 py-8">{t('warehouse.modules.picking.serialPick.noSearchMatch')}</div>
    );
  }

  const startsWithItemNo = scanned.toLowerCase().startsWith(itemNo.toLowerCase());

  return (
    <div className="py-4">
      <div className={`border-2 rounded-lg p-4 ${startsWithItemNo ? 'border-emerald-300 bg-emerald-50' : 'border-amber-300 bg-amber-50'}`}>
        <div className="flex items-start gap-2 mb-3">
          {startsWithItemNo ? (
            <Info size={18} className="text-emerald-600 flex-shrink-0 mt-0.5" />
          ) : (
            <AlertTriangle size={18} className="text-amber-600 flex-shrink-0 mt-0.5" />
          )}
          <div className="flex-1 text-sm">
            <div className={`font-semibold ${startsWithItemNo ? 'text-emerald-800' : 'text-amber-800'}`}>
              {t('warehouse.modules.picking.serialPick.newSerialTitle')}
            </div>
            <div className="mt-1 text-gray-700">
              <span className="font-mono font-medium">{scanned}</span>
            </div>
            {!startsWithItemNo && (
              <div className="mt-2 text-xs text-amber-800">
                {t('warehouse.modules.picking.serialPick.serialMustStartWith')} <span className="font-mono font-semibold">{itemNo}</span>.
                {' '}{t('warehouse.modules.picking.serialPick.checkLabel')}
              </div>
            )}
            {startsWithItemNo && (
              <div className="mt-2 text-xs text-emerald-800">
                {t('warehouse.modules.picking.serialPick.registerReplaceHint')}
              </div>
            )}
          </div>
        </div>

        <button
          onClick={() => onRegister(scanned)}
          disabled={!startsWithItemNo || confirming}
          className="w-full py-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors font-medium flex items-center justify-center gap-2"
        >
          {confirming ? (
            <>
              <Loader2 className="animate-spin" size={18} />
              {t('warehouse.modules.picking.serialPick.registering')}
            </>
          ) : (
            <>
              <CheckCircle size={18} />
              {t('warehouse.modules.picking.serialPick.registerAndPick')}
            </>
          )}
        </button>
      </div>
    </div>
  );
};

interface ExtendedPickingLine extends PListLineDto {
  itemDescription?: string;
}

interface SerialPickModalProps {
  isOpen: boolean;
  line: ExtendedPickingLine | null;
  pListId: number;
  onClose: () => void;
  onSerialPicked: (serialNo: string) => void;
}

interface SerialItem {
  serialNo: string;
  binCode: string;
  warehouseId: string;
}

export const SerialPickModal: React.FC<SerialPickModalProps> = ({
  isOpen,
  line,
  pListId,
  onClose,
  onSerialPicked
}) => {
  const { t } = useTranslation();
  const [availableSerials, setAvailableSerials] = useState<SerialItem[]>([]);
  const [filteredSerials, setFilteredSerials] = useState<SerialItem[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedSerial, setSelectedSerial] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Load available serial numbers when modal opens
  useEffect(() => {
    if (isOpen && line) {
      loadAvailableSerials();
      setSearchTerm('');
      setSelectedSerial(null);
      setError(null);
    }
  }, [isOpen, line]);

  // Focus input when modal opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Filter serials when search term changes
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredSerials(availableSerials);
    } else {
      const term = searchTerm.toLowerCase();
      setFilteredSerials(
        availableSerials.filter(s => s.serialNo.toLowerCase().includes(term))
      );
    }
  }, [searchTerm, availableSerials]);

  const loadAvailableSerials = async () => {
    if (!line?.itemNo || !line?.binCode || !line?.warehouseID) {
      setError(t('warehouse.modules.picking.serialPick.missingItemInfo'));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const serials = await stcTransService.getSerialNumbersAtBin(
        line.itemNo,
        line.warehouseID,
        line.binCode
      );
      setAvailableSerials(serials);
      setFilteredSerials(serials);

      // Pre-select requested serial from order if it matches an available serial
      if (line.requestedSerialNo && serials.length > 0) {
        const match = serials.find(
          s => s.serialNo.toLowerCase() === line.requestedSerialNo!.toLowerCase()
        );
        if (match) {
          setSelectedSerial(match.serialNo);
        }
      }

      if (serials.length === 0) {
        setError(t('warehouse.modules.picking.serialPick.noSerialsAtBin'));
      }
    } catch (err: any) {
      console.error('Error loading serial numbers:', err);
      setError(err.message || t('warehouse.modules.picking.serialPick.couldNotLoadSerials'));
    } finally {
      setLoading(false);
    }
  };

  const handleSelectSerial = (serialNo: string) => {
    if (isDummySerial(serialNo)) {
      setError(t('warehouse.modules.picking.serialPick.dummySerialError'));
      return;
    }
    setError(null);
    setSelectedSerial(serialNo);
  };

  const handleConfirm = async () => {
    if (!selectedSerial) return;

    setConfirming(true);
    try {
      onSerialPicked(selectedSerial);
    } finally {
      setConfirming(false);
    }
  };

  const handleScanInput = async (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && searchTerm.trim()) {
      const scanned = searchTerm.trim();

      // 1. Exact match i lista?
      const exactMatch = availableSerials.find(
        s => s.serialNo.toLowerCase() === scanned.toLowerCase()
      );
      if (exactMatch) {
        // Blokker skanning av dummy — operatør må oppgi ekte serienummer
        if (isDummySerial(exactMatch.serialNo)) {
          setError(t('warehouse.modules.picking.serialPick.dummySerialError'));
          return;
        }
        setSelectedSerial(exactMatch.serialNo);
        onSerialPicked(exactMatch.serialNo);
        return;
      }

      // 2. Ingen match — sjekk om det finnes dummies på lokasjonen og prøv silent erstatning
      const hasDummies = availableSerials.some(s => isDummySerial(s.serialNo));
      if (hasDummies && line) {
        try {
          setConfirming(true);
          const result = await pickingService.replaceDummyAndPickSerial(
            pListId,
            line.pListLineNo,
            scanned
          );
          if (result.replaced && result.newSerialNo) {
            onSerialPicked(result.newSerialNo);
            return;
          }
          // Backend sa nei (f.eks. prefix matcher ikke varenummer) → vanlig feilmelding
          setError(t('warehouse.modules.picking.serialPick.serialNotAtBin'));
        } catch (err: any) {
          setError(err?.message || t('warehouse.modules.picking.serialPick.registrationError'));
        } finally {
          setConfirming(false);
        }
        return;
      }

      // 3. Ingen dummies — gammel oppførsel
      if (filteredSerials.length === 1 && !isDummySerial(filteredSerials[0].serialNo)) {
        setSelectedSerial(filteredSerials[0].serialNo);
        onSerialPicked(filteredSerials[0].serialNo);
      } else {
        setError(t('warehouse.modules.picking.serialPick.serialNotAtBin'));
      }
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  if (!isOpen || !line) return null;

  const pickedCount = Math.floor(line.qtyPicked);
  const totalRequired = Math.floor(line.qty);
  const remaining = totalRequired - pickedCount;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-0 sm:p-4">
      <div className="bg-white w-full h-full sm:h-auto sm:rounded-lg sm:max-w-lg sm:max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b bg-emerald-50">
          <div className="flex items-center gap-2">
            <Hash className="text-emerald-600" size={24} />
            <h3 className="text-lg font-semibold">{t('warehouse.modules.picking.serialPick.title')}</h3>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
          >
            <X size={24} />
          </button>
        </div>

        {/* Item info */}
        <div className="p-4 bg-gray-50 border-b">
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div>
              <span className="text-gray-500">{t('common.fields.itemNumber')}:</span>
              <span className="ml-2 font-medium">{line.itemNo}</span>
            </div>
            <div>
              <span className="text-gray-500">{t('warehouse.general.bin')}:</span>
              <span className="ml-2 font-medium">{line.binCode}</span>
            </div>
            <div>
              <span className="text-gray-500">{t('warehouse.modules.picking.picked')}:</span>
              <span className="ml-2 font-medium">{pickedCount} / {totalRequired}</span>
            </div>
            <div>
              <span className="text-gray-500">{t('warehouse.modules.picking.labels.remaining')}:</span>
              <span className="ml-2 font-medium text-orange-600">{remaining}</span>
            </div>
          </div>
          {line.itemDescription && (
            <div className="mt-1 text-sm text-gray-600 truncate">
              {line.itemDescription}
            </div>
          )}
        </div>

        {/* Requested serial info banner */}
        {line.requestedSerialNo && (
          <div className="px-4 py-2 bg-blue-50 border-b border-blue-100 flex items-center gap-2 text-sm text-blue-700">
            <Info size={16} className="flex-shrink-0" />
            <span>{t('warehouse.modules.picking.serialPick.requestedSerial')}: <strong className="font-mono">{line.requestedSerialNo}</strong></span>
          </div>
        )}

        {/* Search/scan input */}
        <div className="p-4 border-b">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
            <ScanTextInput
              ref={inputRef}
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setError(null);
              }}
              onKeyDown={handleScanInput}
              placeholder={t('warehouse.modules.picking.serialPick.searchPlaceholder')}
              className="w-full pl-10 pr-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              disabled={confirming}
            />
          </div>
          {availableSerials.some(s => isDummySerial(s.serialNo)) && !error && (
            <div className="mt-2 flex items-start gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2">
              <AlertTriangle size={14} className="flex-shrink-0 mt-0.5" />
              <span>
                {t('warehouse.modules.picking.serialPick.dummyLocationWarningPrefix')} <span className="font-mono font-semibold">{line.itemNo}</span>.
              </span>
            </div>
          )}
          {error && (
            <div className="mt-2 flex items-center gap-2 text-red-600 text-sm">
              <AlertCircle size={16} />
              {error}
            </div>
          )}
        </div>

        {/* Serial numbers list */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center gap-2 text-gray-500 py-8">
              <Loader2 className="animate-spin" size={20} />
              {t('warehouse.modules.picking.serialPick.loadingSerials')}
            </div>
          ) : filteredSerials.length === 0 && !error ? (
            <RegisterNewSerialBox
              scanned={searchTerm.trim()}
              itemNo={line.itemNo ?? ''}
              hasDummies={availableSerials.some(s => isDummySerial(s.serialNo))}
              confirming={confirming}
              onRegister={async (scanned) => {
                try {
                  setConfirming(true);
                  setError(null);
                  const result = await pickingService.replaceDummyAndPickSerial(
                    pListId, line.pListLineNo, scanned);
                  if (result.replaced && result.newSerialNo) {
                    onSerialPicked(result.newSerialNo);
                  } else {
                    setError(result.message || t('warehouse.modules.picking.serialPick.serialNotAtBin'));
                  }
                } catch (err: any) {
                  setError(err?.message || t('warehouse.modules.picking.serialPick.registrationError'));
                } finally {
                  setConfirming(false);
                }
              }}
            />
          ) : (
            <div className="space-y-2">
              {filteredSerials.map((serial) => {
                const dummy = isDummySerial(serial.serialNo);
                return (
                  <button
                    key={serial.serialNo}
                    onClick={() => handleSelectSerial(serial.serialNo)}
                    disabled={confirming || dummy}
                    title={dummy ? t('warehouse.modules.picking.serialPick.dummyTooltip') : undefined}
                    className={`w-full flex items-center justify-between p-3 rounded-lg border transition-all text-left ${
                      dummy
                        ? 'border-amber-300 bg-amber-50/60 cursor-not-allowed opacity-70'
                        : selectedSerial === serial.serialNo
                        ? 'border-emerald-500 bg-emerald-50 ring-2 ring-emerald-200'
                        : 'border-gray-200 hover:border-emerald-300 hover:bg-emerald-50/50'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      {dummy && <AlertTriangle className="text-amber-600 flex-shrink-0" size={16} />}
                      <span className={`font-mono font-medium ${dummy ? 'text-amber-800' : 'text-gray-900'}`}>
                        {serial.serialNo}
                      </span>
                      {dummy && (
                        <span className="text-xs bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded font-medium">
                          {t('warehouse.modules.picking.serialPick.dummyBadge')}
                        </span>
                      )}
                      {!dummy && line.requestedSerialNo && serial.serialNo.toLowerCase() === line.requestedSerialNo.toLowerCase() && (
                        <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded font-medium">
                          {t('warehouse.modules.picking.serialPick.fromOrder')}
                        </span>
                      )}
                    </div>
                    {!dummy && selectedSerial === serial.serialNo && (
                      <CheckCircle className="text-emerald-600 flex-shrink-0" size={20} />
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t bg-gray-50 flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
          >
            {t('common.buttons.cancel')}
          </button>
          <button
            onClick={handleConfirm}
            disabled={!selectedSerial || confirming}
            className="flex-1 py-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors font-medium flex items-center justify-center gap-2"
          >
            {confirming ? (
              <>
                <Loader2 className="animate-spin" size={18} />
                {t('warehouse.modules.picking.serialPick.confirming')}
              </>
            ) : (
              t('warehouse.modules.picking.serialPick.confirmPick')
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
